import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

// Common country codes with their dialing codes
const COUNTRIES = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
];

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
}

export function PhoneInput({
  value = '',
  onChange,
  placeholder = 'Phone number',
  className,
  error = false,
  disabled = false,
  required = false,
}: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Detect country by dial code prefix
  const detectCountryByNumber = (full: string) => {
    const sanitized = full.replace(/\s/g, '');
    const match = COUNTRIES
      .slice()
      .sort((a, b) => b.dialCode.length - a.dialCode.length) // prefer longer codes
      .find(c => sanitized.startsWith(c.dialCode));
    return match || selectedCountry;
  };

  // Parse initial value
  useEffect(() => {
    if (value) {
      const country = detectCountryByNumber(value);
      if (country) {
        setSelectedCountry(country);
        setPhoneNumber(value.slice(country.dialCode.length).trim());
      } else {
        // Default to US if no country code detected
        setPhoneNumber(value.replace(/^\+\d+\s*/, ''));
      }
    }
  }, []);

  // Update parent when value changes
  useEffect(() => {
    const fullNumber = phoneNumber ? `${selectedCountry.dialCode} ${phoneNumber}` : '';
    if (onChange && fullNumber !== value) {
      onChange(fullNumber);
    }
  }, [selectedCountry, phoneNumber]);

  const formatPhoneNumber = (input: string) => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');
    
    // Apply US formatting for US/CA numbers
    if (selectedCountry.code === 'US' || selectedCountry.code === 'CA') {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    
    // For other countries, just group digits nicely
    return digits.replace(/(\d{3})(?=\d)/g, '$1 ');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // If user pastes/starts with +code, auto-detect country and strip code portion
    if (input.startsWith('+')) {
      const match = input.match(/^\+\d{1,4}/);
      if (match) {
        const dial = match[0];
        const country = detectCountryByNumber(dial);
        setSelectedCountry(country);
        const remainder = input.slice(dial.length).trim();
        const formatted = formatPhoneNumber(remainder);
        setPhoneNumber(formatted);
        return;
      }
    }
    const formatted = formatPhoneNumber(input);
    setPhoneNumber(formatted);
  };

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'flex items-center rounded-md border bg-background',
          error ? 'border-error-500 focus-within:ring-error-500' : 'border-input focus-within:ring-ring',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {/* Country Selector */}
        <button
          type="button"
          onClick={() => !disabled && setShowDropdown(!showDropdown)}
          disabled={disabled}
          className={cn(
            'flex items-center gap-2 px-3 py-2 border-r hover:bg-gray-50',
            disabled && 'cursor-not-allowed'
          )}
        >
          <span className="text-lg">{selectedCountry.flag}</span>
          <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
          <ChevronDown className="w-4 h-4 text-gray-500" />
        </button>

        {/* Phone Number Input */}
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={cn(
            'flex-1 px-3 py-2 text-sm bg-transparent',
            'placeholder:text-muted-foreground',
            'focus:outline-none',
            'disabled:cursor-not-allowed'
          )}
        />
      </div>

      {/* Country Dropdown */}
      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute left-0 top-full mt-1 w-64 max-h-64 overflow-y-auto bg-background border rounded-md shadow-lg z-20">
            {COUNTRIES.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => {
                  setSelectedCountry(country);
                  setShowDropdown(false);
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50',
                  selectedCountry.code === country.code && 'bg-gray-100'
                )}
              >
                <span className="text-lg">{country.flag}</span>
                <span className="flex-1 text-left">{country.name}</span>
                <span className="text-gray-500">{country.dialCode}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
